// elementInfoMeta.js
export const AGG = {
  max: "maximum",
  min: "minimum",
  mean: "average",
  sum: "sum",
  mode: "mode",
  vectorMean: "vectorAverage", // circular mean for directions
  last: "last",
  first: "first"
};

/**
 * Helper: resolve a unit string for the given unitGroup.
 * Known VC unit groups: 'metric', 'us', 'uk'
 */
export function resolveUnit(u, unitGroup = "metric") {
  if (!u || typeof u !== "object") return null;
  return u[unitGroup] ?? u.metric ?? null;
}


/**
 * ELEMENT_META with:
 *  - unit: { metric, us, uk }
 *  - precision: number | null
 *  - aggregation: AGG.*
 *
 * Notes on conventions (aligned to VC docs & common rollups):
 * - Temperatures: max/min/mean as appropriate
 * - Precipitation/solar energy: sum
 * - Speeds/visibility/humidity/pressure/radiation: average
 * - Wind direction: vectorAverage (circular)
 * - Probabilities/indices (uvindex, precipprob, severerisk): maximum
 * - Categorical text (conditions, preciptype): mode
 * - Times (sunrise/sunset): none
 */
export const ELEMENT_META = {
  // --- Core Weather ---
  tempmax: {
    label: "Maximum Temperature",
    description: "The maximum air temperature for the period.",
    unit: { metric: "°C", us: "°F", uk: "°C" },
    precision: 1,
    aggregation: AGG.max,
    sections: ["days"]
  },
  tempmin: {
    label: "Minimum Temperature",
    description: "The minimum air temperature for the period.",
    unit: { metric: "°C", us: "°F", uk: "°C" },
    precision: 1,
    aggregation: AGG.min,
    sections: ["days"]
  },
  temp: {
    label: "Average Temperature",
    description: "Mean temperature over the period.",
    unit: { metric: "°C", us: "°F", uk: "°C" },
    precision: 1,
    aggregation: AGG.mean
  },
  dew: {
    label: "Dew Point",
    description: "Temperature at which air becomes saturated with moisture.",
    unit: { metric: "°C", us: "°F", uk: "°C" },
    precision: 1,
    aggregation: AGG.mean
  },
  feelslikemax: {
    label: "Maximum Feels Like",
    description: "Maximum apparent temperature (heat index / wind chill).",
    unit: { metric: "°C", us: "°F", uk: "°C" },
    precision: 1,
    aggregation: AGG.max,
    sections: ["days"]
  },
  feelslikemin: {
    label: "Minimum Feels Like",
    description: "Minimum apparent temperature (heat index / wind chill).",
    unit: { metric: "°C", us: "°F", uk: "°C" },
    precision: 1,
    aggregation: AGG.min,
    sections: ["days"]
  },
  feelslike: {
    label: "Feels Like",
    description: "Apparent temperature for the period.",
    unit: { metric: "°C", us: "°F", uk: "°C" },
    precision: 1,
    aggregation: AGG.mean
  },

  precip: {
    label: "Precipitation",
    description: "Total precipitation (rain, snow water equivalent, etc.).",
    unit: { metric: "mm", us: "in", uk: "mm" },
    precision: 1,
    aggregation: AGG.sum
  },
  precipremote: {
    label: "Precipitation (Remote)",
    description: "Precipitation measured by radar or other remote sensing methods.",
    unit: { metric: "mm", us: "in", uk: "mm" },
    precision: 1,
    aggregation: AGG.sum
  },
  precipprob: {
    label: "Precipitation Chance",
    description: "Probability of precipitation during the period.",
    unit: { metric: "%", us: "%", uk: "%" },
    precision: 0,
    aggregation: AGG.max
  },
  precipcover: {
    label: "Precipitation Cover",
    description: "Percent of time with measurable precipitation.",
    unit: { metric: "%", us: "%", uk: "%" },
    precision: 0,
    aggregation: AGG.mean,
    sections: ["days"]
  },
  preciptype: {
    label: "Precipitation Type",
    description: "Type(s) of precipitation observed or forecast.",
    format: "array"
  },

  snow: {
    label: "Snowfall",
    description: "New snowfall during the period (water-equivalent conversion varies by source).",
    unit: { metric: "cm", us: "in", uk: "cm" },
    precision: 1,
    aggregation: AGG.sum
  },
  snowdepth: {
    label: "Snow Depth",
    description: "Depth of snow on the ground.",
    unit: { metric: "cm", us: "in", uk: "cm" },
    precision: 1,
    aggregation: AGG.mean // some products use 'last'; mean is a safe default
  },

  windspeed: {
    label: "Wind Speed",
    description: "Average wind speed over the period.",
    unit: { metric: "km/h", us: "mph", uk: "mph" },
    precision: 1,
    aggregation: AGG.mean
  },
  windspeedmax: {
    label: "Wind Speed (Maximum)",
    description: "Maximum wind speed over the period.",
    unit: { metric: "km/h", us: "mph", uk: "mph" },
    precision: 1,
    aggregation: AGG.mean
  },
    windspeedmin: {
    label: "Wind Speed (Minimum)",
    description: "Minimum wind speed over the period.",
    unit: { metric: "km/h", us: "mph", uk: "mph" },
    precision: 1,
    aggregation: AGG.mean
  },
    windspeedmean: {
    label: "Wind Speed (Mean)",
    description: "Average wind speed over the period.",
    unit: { metric: "km/h", us: "mph", uk: "mph" },
    precision: 1,
    aggregation: AGG.mean
  },
  windgust: {
    label: "Wind Gust",
    description: "Peak wind gust during the period.",
    unit: { metric: "km/h", us: "mph", uk: "mph" },
    precision: 1,
    aggregation: AGG.max
  },
  winddir: {
    label: "Wind Direction",
    description: "Direction from which the wind blows (degrees from North).",
    unit: { metric: "°", us: "°", uk: "°" },
    precision: 0,
    aggregation: AGG.vectorMean
  },
  sealevelpressure: {
    label: "Sea Level Pressure",
    description: "Atmospheric pressure adjusted to sea level.",
    unit: { metric: "mb (hPa)", us: "mb (hPa)", uk: "mb (hPa)" }, // VC commonly uses millibars / hPa
    precision: 0,
    aggregation: AGG.mean
  },
  visibility: {
    label: "Visibility",
    description: "Horizontal visibility.",
    unit: { metric: "km", us: "mi", uk: "mi" },
    precision: 1,
    aggregation: AGG.mean
  },
  cloudcover: {
    label: "Cloud Cover",
    description: "Percent of sky covered by clouds.",
    unit: { metric: "%", us: "%", uk: "%" },
    precision: 0,
    aggregation: AGG.mean
  },
  humidity: {
    label: "Relative Humidity",
    description: "Percent of saturation.",
    unit: { metric: "%", us: "%", uk: "%" },
    precision: 0,
    aggregation: AGG.mean
  },
  pressure: {
    label: "Sea Level Pressure",
    description: "Atmospheric pressure adjusted to sea level.",
    unit: { metric: "mb", us: "mb", uk: "mb" }, // VC commonly uses millibars / hPa
    precision: 0,
    aggregation: AGG.mean
  },

  solarradiation: {
    label: "Solar Radiation",
    description: "Instantaneous solar power per square meter.",
    unit: { metric: "W/m²", us: "W/m²", uk: "W/m²" },
    precision: 0,
    aggregation: AGG.mean
  },
  solarenergy: {
    label: "Solar Energy",
    description: "Integrated solar energy over the period.",
    unit: { metric: "MJ/m²", us: "MJ/m²", uk: "MJ/m²" },
    precision: 2,
    aggregation: AGG.sum
  },
  uvindex: {
    label: "UV Index",
    description: "Ultraviolet exposure index (0–10).",
    precision: 1,
    aggregation: AGG.max
  },
  uvindex2: {
    label: "UV Index",
    description: "Alternative UV index element that uses the algorithms and models used by the US National Weather Service.",
    precision: 1,
    aggregation: AGG.max
  },
  severerisk: {
    label: "Severe Risk",
    description: "A value between 0 and 100 representing the risk of convective storms (e.g. thunderstorms, hail and tornadoes).",
    aggregation: AGG.max
  },
  cin: {
    label: "Convective Inhibition (CIN)",
    description: "A measure of the stability of the atmosphere and its potential to support convection.",
    precision: 1,
    aggregation: AGG.max
  },
  cape : {
    label: "Convective Available Potential Energy (CAPE)",
    description: "The amount of energy available for convection.",
    precision: 1,
    aggregation: AGG.max
  },


  sunrise: {
    label: "Sunrise",
    description: "Local time of sunrise.",
    sections: ["days"]
  },
  sunset: {
    label: "Sunset",
    description: "Local time of sunset.",
    sections: ["days"]
    
  },
  moonphase: {
    label: "Moon Phase",
    description: "Fractional moon phase (0 new → 1 full).",
    
    precision: 2,
    aggregation: AGG.mean,
    sections: ["days"]
  },
  moonrise: {
    label: "Moonrise",
    description: "Local time of moonrise.",
    sections: ["days"]
  },
  moonset: {
    label: "Moonset",
    description: "Local time of moonset.",
    sections: ["days"]
  },
  icon: {
    label: "Weather Icon",
    description: "Icon code for conditions.",
    
  },
  conditions: {
    label: "Conditions",
    description: "Short text description of weather conditions for the period.",
    
  },
  
  description: {
    label: "Description",
    description: "Longer text description.",
    
  },
  source: {
    label: "Source",
    description: "Data source category (e.g., 'obs', 'fcst').",
    
  },
  stations: {
    label: "Stations",
    description: "Weather station identifiers.",
    format: "array"
    
  },

  // --- Air Quality ---
  aqius: {
    label: "AQI (US)",
    description: "US EPA Air Quality Index.",
    
    precision: 0,
    aggregation: AGG.max
  },
  aqieur: {
    label: "AQI (EU)",
    description: "European Air Quality Index.",
    
    precision: 0,
    aggregation: AGG.max
  },
  aqielement: {
    label: "AQI (Element)",
    description: "Element responsible for AQI.",
    precision: 0,
    aggregation: AGG.max
  },
  pm1: {
    label: "PM1",
    description: "Particles <1µm concentration.",
    unit: { metric: "µg/m³", us: "µg/m³", uk: "µg/m³" },
    precision: 1,
    aggregation: AGG.mean
  },
  pm2p5: {
    label: "PM2.5",
    description: "Particles <2.5µm concentration.",
    unit: { metric: "µg/m³", us: "µg/m³", uk: "µg/m³" },
    precision: 1,
    aggregation: AGG.mean
  },
  pm10: {
    label: "PM10",
    description: "Particles <10µm concentration.",
    unit: { metric: "µg/m³", us: "µg/m³", uk: "µg/m³" },
    precision: 1,
    aggregation: AGG.mean
  },
  o3: {
    label: "Ozone (O₃)",
    description: "Ozone concentration.",
    unit: { metric: "ppb", us: "ppb", uk: "ppb" },
    precision: 1,
    aggregation: AGG.mean
  },
  no2: {
    label: "Nitrogen Dioxide (NO₂)",
    description: "NO₂ concentration.",
    unit: { metric: "ppb", us: "ppb", uk: "ppb" },
    precision: 1,
    aggregation: AGG.mean
  },
  so2: {
    label: "Sulfur Dioxide (SO₂)",
    description: "SO₂ concentration.",
    unit: { metric: "ppb", us: "ppb", uk: "ppb" },
    precision: 1,
    aggregation: AGG.mean
  },
  co: {
    label: "Carbon Monoxide (CO)",
    description: "CO concentration.",
    unit: { metric: "ppb", us: "ppb", uk: "ppb" },
    precision: 1,
    aggregation: AGG.mean
  },
  smokesfc: {
    label: "Surface Smoke",
    description: "Surface-level smoke concentration from wildfire and biomass burning.",
    unit: { metric: "µg/m³", us: "µg/m³", uk: "µg/m³" },
    precision: 1,
    aggregation: AGG.mean
  },
  smokecol: {
    label: "Smoke Column",
    description: "Vertically-integrated smoke column density from wildfire and biomass burning.",
    unit: { metric: "µg/m³", us: "µg/m³", uk: "µg/m³" },
    precision: 1,
    aggregation: AGG.mean
  },
  dust: {
    label: "Dust",
    description: "Surface-level dust concentration.",
    unit: { metric: "µg/m³", us: "µg/m³", uk: "µg/m³" },
    precision: 1,
    aggregation: AGG.mean
  },
  aod550: {
    label: "Aerosol Optical Depth (550nm)",
    description: "Aerosol optical depth at 550nm. Dimensionless measure of atmospheric aerosol loading affecting solar radiation and visibility.",
    precision: 3,
    aggregation: AGG.mean
  },
  hainesindex: {
    label: "Haines Index",
    description: "Lower atmosphere stability and dryness index indicating wildfire growth potential. Scale 2-6, where 6 is highest risk.",
    precision: 0,
    aggregation: AGG.max
  },

  // --- Agriculture / Comfort ---
  degreedays: {
    label: "Degree Days",
    description: "Accumulated degree days over the period.",
    unit: { metric: "°C·day", us: "°F·day", uk: "°C·day" },
    precision: 1,
    aggregation: AGG.sum
  },
  accdegreedays: {
    label: "Accumulated Degree Days",
    description: "Seasonal accumulated degree days.",
    unit: { metric: "°C·day", us: "°F·day", uk: "°C·day" },
    precision: 1,
    aggregation: AGG.last // running total; last of period is typically most useful
  },
  tempwet: {
    label: "Wet Bulb Temperature",
    description: "Thermodynamic wet-bulb temperature.",
    unit: { metric: "°C", us: "°F", uk: "°C" },
    precision: 1,
    aggregation: AGG.mean
  },
  deltat: {
    label: "Delta-T",
    description: "Dry bulb minus wet bulb.",
    unit: { metric: "°C", us: "°F", uk: "°C" },
    precision: 1,
    aggregation: AGG.mean
  },
  et0: {
    label: "Evapotranspiration (ETo)",
    description: "Reference evapotranspiration (Penman–Monteith).",
    unit: { metric: "mm", us: "in", uk: "mm" },
    precision: 2,
    aggregation: AGG.sum
  },

  // Soil temperature (layered)
  soiltemp01: {
    label: "Soil Temperature (0–10 cm)",
    description: "Average soil temperature 0–0.1 m.",
    unit: { metric: "°C", us: "°F", uk: "°C" },
    precision: 1,
    aggregation: AGG.mean
  },
  soiltemp04: {
    label: "Soil Temperature (10–40 cm)",
    description: "Average soil temperature 0.1–0.4 m.",
    unit: { metric: "°C", us: "°F", uk: "°C" },
    precision: 1,
    aggregation: AGG.mean
  },
  soiltemp10: {
    label: "Soil Temperature (40–100 cm)",
    description: "Average soil temperature 0.4–1.0 m.",
    unit: { metric: "°C", us: "°F", uk: "°C" },
    precision: 1,
    aggregation: AGG.mean
  },
  soiltemp20: {
    label: "Soil Temperature (100–200 cm)",
    description: "Average soil temperature 1.0–2.0 m.",
    unit: { metric: "°C", us: "°F", uk: "°C" },
    precision: 1,
    aggregation: AGG.mean
  },

  // Soil moisture (depth) & volumetric fraction
  soilmoisture01: {
    label: "Soil Moisture (0–10 cm)",
    description: "Soil moisture depth 0–0.1 m.",
    unit: { metric: "mm", us: "in", uk: "mm" },
    precision: 1,
    aggregation: AGG.mean
  },
  soilmoisture04: {
    label: "Soil Moisture (10–40 cm)",
    description: "Soil moisture depth 0.1–0.4 m.",
    unit: { metric: "mm", us: "in", uk: "mm" },
    precision: 1,
    aggregation: AGG.mean
  },
  soilmoisture10: {
    label: "Soil Moisture (40–100 cm)",
    description: "Soil moisture depth 0.4–1.0 m.",
    unit: { metric: "mm", us: "in", uk: "mm" },
    precision: 1,
    aggregation: AGG.mean
  },
  soilmoisture20: {
    label: "Soil Moisture (100–200 cm)",
    description: "Soil moisture depth 1.0–2.0 m.",
    unit: { metric: "mm", us: "in", uk: "mm" },
    precision: 1,
    aggregation: AGG.mean
  },
  soilmoisturevol01: {
    label: "Volumetric Soil Moisture (0–10 cm)",
    description: "Volumetric water content 0–0.1 m.",
    unit: { metric: "fraction", us: "fraction", uk: "fraction" },
    precision: 2,
    aggregation: AGG.mean
  },
  soilmoisturevol04: {
    label: "Volumetric Soil Moisture (10–40 cm)",
    description: "Volumetric water content 0.1–0.4 m.",
    unit: { metric: "fraction", us: "fraction", uk: "fraction" },
    precision: 2,
    aggregation: AGG.mean
  },
  soilmoisturevol10: {
    label: "Volumetric Soil Moisture (40–100 cm)",
    description: "Volumetric water content 0.4–1.0 m.",
    unit: { metric: "fraction", us: "fraction", uk: "fraction" },
    precision: 2,
    aggregation: AGG.mean
  },
  soilmoisturevol20: {
    label: "Volumetric Soil Moisture (100–200 cm)",
    description: "Volumetric water content 1.0–2.0 m.",
    unit: { metric: "fraction", us: "fraction", uk: "fraction" },
    precision: 2,
    aggregation: AGG.mean
  },

  // --- Extended Wind / Energy heights ---
  windspeed50: {
    label: "Wind Speed (50 m)",
    description: "Wind speed at 50 m AGL.",
    unit: { metric: "km/h", us: "mph", uk: "mph" },
    precision: 1,
    aggregation: AGG.mean
  },
  winddir50: {
    label: "Wind Direction (50 m)",
    description: "Wind direction at 50 m AGL.",
    unit: { metric: "°", us: "°", uk: "°" },
    precision: 0,
    aggregation: AGG.vectorMean
  },
  windspeed80: {
    label: "Wind Speed (80 m)",
    description: "Wind speed at 80 m AGL.",
    unit: { metric: "km/h", us: "mph", uk: "mph" },
    precision: 1,
    aggregation: AGG.mean
  },
  winddir80: {
    label: "Wind Direction (80 m)",
    description: "Wind direction at 80 m AGL.",
    unit: { metric: "°", us: "°", uk: "°" },
    precision: 0,
    aggregation: AGG.vectorMean
  },
  windspeed100: {
    label: "Wind Speed (100 m)",
    description: "Wind speed at 100 m AGL.",
    unit: { metric: "km/h", us: "mph", uk: "mph" },
    precision: 1,
    aggregation: AGG.mean
  },
  winddir100: {
    label: "Wind Direction (100 m)",
    description: "Wind direction at 100 m AGL.",
    unit: { metric: "°", us: "°", uk: "°" },
    precision: 0,
    aggregation: AGG.vectorMean
  },

  // --- Solar components / angles ---
  dniradiation: {
    label: "Direct Normal Irradiance (DNI)",
    description: "Direct beam solar irradiance (normal to sun).",
    unit: { metric: "W/m²", us: "W/m²", uk: "W/m²" },
    precision: 0,
    aggregation: AGG.mean
  },
  difradiation: {
    label: "Diffuse Solar Radiation",
    description: "Diffuse solar irradiance.",
    unit: { metric: "W/m²", us: "W/m²", uk: "W/m²" },
    precision: 0,
    aggregation: AGG.mean
  },
  ghiradiation: {
    label: "Global Horizontal Irradiance (GHI)",
    description: "Total solar irradiance on horizontal surface.",
    unit: { metric: "W/m²", us: "W/m²", uk: "W/m²" },
    precision: 0,
    aggregation: AGG.mean
  },
  gtiradiation: {
    label: "Global Tilted Irradiance (GTI)",
    description: "Irradiance on a tilted surface.",
    unit: { metric: "W/m²", us: "W/m²", uk: "W/m²" },
    precision: 0,
    aggregation: AGG.mean
  },
  sunazimuth: {
    label: "Sun Azimuth",
    description: "Sun azimuth (degrees from North).",
    unit: { metric: "°", us: "°", uk: "°" },
    precision: 0,
    aggregation: AGG.mean
  },
  sunelevation: {
    label: "Sun Elevation",
    description: "Elevation angle of the sun.",
    unit: { metric: "°", us: "°", uk: "°" },
    precision: 1,
    aggregation: AGG.mean
  },

  // --- Time / Location meta (mostly non-aggregating) ---
  datetime: {
    label: "Date / Time",
    description: "Local timestamp (ISO-8601).",
    
  },
  datetimeEpoch: {
    label: "Date / Time (Epoch)",
    description: "Seconds since 1970-01-01 UTC.",
    unit: { metric: "secs", us: "secs", uk: "secs" },
    precision: 0
  },
  timezone: {
    label: "Timezone",
    description: "Returned timezone string.",
    
  },
  tzoffset: {
    label: "Timezone Offset",
    description: "Offset from UTC.",
    unit: { metric: "h", us: "h", uk: "h" },
    precision: 1
  },
  queryCost: {
    label: "Query Cost",
    description: "Cost/credits for the request.",
    
    aggregation: AGG.sum
  },
  latitude: {
    label: "Latitude",
    description: "Location latitude. Positive values are North of the equator.",
    unit: { metric: "°", us: "°", uk: "°" },
    precision: 5,
    aggregation: AGG.mean
  },
  longitude: {
    label: "Longitude",
    description: "Location longitude. Positive values are East of the Prime Meridian.",
    unit: { metric: "°", us: "°", uk: "°" },
    precision: 5,
    aggregation: AGG.mean
  },
  elevation: {
    label: "Elevation",
    description: "Elevation of the location.",
    unit: { metric: "m", us: "ft", uk: "m" },
    precision: 1,
    aggregation: AGG.mean
  },
  resolvedAddress: {
    label: "Resolved Address",
    description: "Canonicalized address.",
    
  },
  address: {
    label: "Requested Address",
    description: "User-provided address string.",
    
  },
  name: {
    label: "Location Name",
    description: "Display name for the location.",
    
  },
  wavedir: {
    label: "Wave Direction",
    description: "Direction from which the waves are coming (degrees from North).", 
    unit: { metric: "°", us: "°", uk: "°" },
    precision: 0,
    aggregation: AGG.vectorMean
  },
  waveheight: {
    label: "Wave Height", 
    description: "Significant wave height (average of highest third of waves).",
    unit: { metric: "m", us: "ft", uk: "m" },
    precision: 1, 
    aggregation: AGG.mean
  },
  waveperiod: {
    label: "Wave Period",   
    description: "Dominant wave period.",
    unit: { metric: "s", us: "s", uk: "s" },
    precision: 1, 
    aggregation: AGG.mean
  },
  maxwaveheight: {
    label: "Maximum Wave Height", 
    description: "Maximum wave height.",
    unit: { metric: "m", us: "ft", uk: "m" },
    precision: 1,
    aggregation: AGG.max
  },
  swellheight: {
    label: "Swell Height", 
    description: "Height of the swell component of the waves.",
    unit: { metric: "m", us: "ft", uk: "m" },
    precision: 1, 
    aggregation: AGG.mean 
  },
  swellperiod: {  
    label: "Swell Period",
    description: "Period of the swell component of the waves.",
    unit: { metric: "s", us: "s", uk: "s" },
    precision: 1, 
    aggregation: AGG.mean 
  },
  swelldir: {
    label: "Swell Direction", 
    description: "Direction from which the swell is coming (degrees from North).",
    unit: { metric: "°", us: "°", uk: "°" },
    precision: 0,
    aggregation: AGG.vectorMean
  }

};
